import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useI18n } from '@/lib/i18n';
import { Image } from '@/components/ui/image';
import { UploadCloud, Loader2, Check, Eye, Pencil } from 'lucide-react';
import HostSocials from '@/components/yakaleo/HostSocials';

const SOCIAL_FIELDS = [
  { key: 'instagram_url', label: 'Instagram', placeholder: 'https://instagram.com/...' },
  { key: 'tiktok_url', label: 'TikTok', placeholder: 'https://tiktok.com/@...' },
  { key: 'twitter_url', label: 'Twitter / X', placeholder: 'https://x.com/...' },
  { key: 'facebook_url', label: 'Facebook', placeholder: 'https://facebook.com/...' },
  { key: 'mixcloud_url', label: 'Mixcloud', placeholder: 'https://mixcloud.com/...' },
  { key: 'soundcloud_url', label: 'SoundCloud', placeholder: 'https://soundcloud.com/...' },
  { key: 'website_url', label: 'Website', placeholder: 'https://...' },
  { key: 'booking_email', label: 'Booking Email', placeholder: 'booking@...' },
];

export default function EditProfileForm({ host, onSaved }) {
  const { t, lang } = useI18n();
  const es = lang === 'es';
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    bio: host.bio || '',
    bio_es: host.bio_es || '',
    photo_url: host.photo_url || '',
    instagram_url: host.instagram_url || '',
    tiktok_url: host.tiktok_url || '',
    twitter_url: host.twitter_url || '',
    facebook_url: host.facebook_url || '',
    mixcloud_url: host.mixcloud_url || '',
    soundcloud_url: host.soundcloud_url || '',
    website_url: host.website_url || '',
    booking_email: host.booking_email || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handlePhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    setError('');
    try {
      const { file_url } = await base44.integrations.Core.UploadPublicFile({ file });
      setField('photo_url', file_url);
    } catch {
      setError(es ? 'Error al subir la foto.' : 'Photo upload failed.');
    }
    setUploadingPhoto(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await base44.functions.invoke('saveHostProfile', form);
      if (res.data?.error) throw new Error(res.data.error);
      if (onSaved) onSaved(res.data.host);
      setOpen(false);
    } catch (e) {
      setError(e.message || (es ? 'Error al guardar.' : 'Save failed.'));
    }
    setSaving(false);
  };

  const previewHost = { ...host, ...form };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-purple-200 text-purple-700 font-semibold text-sm hover:bg-purple-50 transition-colors mb-6"
      >
        <Pencil size={15} /> {es ? 'Editar mi perfil' : 'Edit my profile'}
      </button>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-black text-base font-bold flex items-center gap-2">
          <Pencil size={16} /> {es ? 'Editar mi perfil' : 'Edit my profile'}
        </h3>
        <button onClick={() => setOpen(false)} className="text-neutral-400 hover:text-black text-sm font-medium">
          {t('Cancel')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Edit form */}
        <div className="space-y-4">
          {/* Photo */}
          <div>
            <label className="block text-neutral-700 text-sm font-medium mb-1.5">
              {es ? 'Foto' : 'Photo'}
            </label>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-neutral-100 flex-shrink-0">
                {form.photo_url ? (
                  <img src={form.photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-700 to-black text-white text-2xl font-black">
                    {(host.name || '?').charAt(0)}
                  </div>
                )}
              </div>
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 text-sm font-semibold text-purple-700 hover:bg-purple-50 cursor-pointer transition-colors">
                {uploadingPhoto ? (
                  <><Loader2 size={14} className="animate-spin" /> {es ? 'Subiendo…' : 'Uploading…'}</>
                ) : (
                  <><UploadCloud size={14} /> {es ? 'Cambiar foto' : 'Change photo'}</>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handlePhoto(e.target.files?.[0])}
                  disabled={uploadingPhoto}
                />
              </label>
            </div>
          </div>

          {/* Bio EN */}
          <div>
            <label className="block text-neutral-700 text-sm font-medium mb-1.5">
              {es ? 'Biografía (Inglés)' : 'Bio (English)'}
            </label>
            <textarea
              value={form.bio}
              onChange={(e) => setField('bio', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-neutral-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none text-sm"
            />
          </div>

          {/* Bio ES */}
          <div>
            <label className="block text-neutral-700 text-sm font-medium mb-1.5">
              {es ? 'Biografía (Español)' : 'Bio (Spanish)'}
            </label>
            <textarea
              value={form.bio_es}
              onChange={(e) => setField('bio_es', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-neutral-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none text-sm"
            />
          </div>

          {/* Social links */}
          <div>
            <p className="text-neutral-700 text-sm font-medium mb-2">{es ? 'Redes sociales' : 'Social links'}</p>
            <div className="space-y-2">
              {SOCIAL_FIELDS.map((f) => (
                <input
                  key={f.key}
                  type={f.key === 'booking_email' ? 'email' : 'url'}
                  value={form[f.key]}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="w-full px-3 py-2 rounded-lg border border-neutral-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none text-sm"
                />
              ))}
            </div>
          </div>

          {error && <p className="text-red-600 text-sm font-medium">{error}</p>}

          <button
            onClick={handleSave}
            disabled={saving || uploadingPhoto}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-purple-700 text-white font-bold hover:bg-purple-800 disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <><Loader2 size={16} className="animate-spin" /> {t('Saving…')}</>
            ) : (
              <><Check size={16} /> {es ? 'Guardar perfil' : 'Save profile'}</>
            )}
          </button>
        </div>

        {/* Live preview */}
        <div>
          <p className="text-neutral-500 text-xs font-bold uppercase tracking-wide mb-2 flex items-center gap-1">
            <Eye size={13} /> {es ? 'Vista previa' : 'Preview'}
          </p>
          <div className="bg-neutral-50 rounded-xl border border-neutral-200 p-5">
            <div className="flex flex-col items-center text-center">
              <div className="w-28 h-28 rounded-xl overflow-hidden bg-neutral-100 mb-3">
                {previewHost.photo_url ? (
                  <img src={previewHost.photo_url} alt={previewHost.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-700 to-black text-white text-5xl font-black">
                    {(previewHost.name || '?').charAt(0)}
                  </div>
                )}
              </div>
              <h3 className="text-black text-lg font-black">{previewHost.name}</h3>
              {previewHost.role && (
                <p className="text-purple-700 text-[11px] font-bold tracking-widest uppercase mt-0.5">{previewHost.role}</p>
              )}
              <p className="text-neutral-600 text-sm mt-3 text-left whitespace-pre-line line-clamp-6">
                {(es ? previewHost.bio_es : previewHost.bio) || (es ? 'Sin biografía aún.' : 'No bio yet.')}
              </p>
              <HostSocials host={previewHost} size={18} className="mt-4 justify-center" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}